import type { ColumnDef, Row } from "@tanstack/react-table";
import {
  DateTableDisplay,
  SearchHeader,
} from "~/components/common/table-components";
import { Badge } from "~/components/ui/badge";
import type { Application, TagAttribute } from "~/types/api";
import { NodeTypeEnum, nodeTypeToString } from "./api-util";
import type { NodeTableColumnSettings } from "~/utils/api-util";
import {
  CheckIcon,
  CrossIcon,
  SignalHigh,
  SignalLow,
  SignalMedium,
  UnplugIcon,
} from "lucide-react";
import { isDateAfterHours } from "./date-util";
import useUiNodeState from "~/stores/ui/node";
import { NaBadge } from "~/components/common/NaBadge";
import {
  defaultTableColumns,
  getActionColDefs,
  type TableActionEventType,
} from "./table-util";
import useUserState from "~/stores/user";

export const nodeTableColumnsMap: { [key: string]: ColumnDef<any> } = {
  select: defaultTableColumns["select"],

  nodeType: {
    id: "nodeType",
    accessorKey: "nodeType",
    cell: (info) => <NodeTypeCell nodeType={info.getValue() as number} />,
    header: ({ column }) => (
      <SearchHeader column={column} child="nodes.headers.nodeType" combo={[]} />
    ),
    filterFn: (row, id, filterValue) => {
      if (!filterValue || filterValue.length === 0) return true;

      for (var f of filterValue) {
        if (nodeTypeToString(row.getValue(id)) === f) return true;
      }

      return false;
    },
  },
  name: {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <SearchHeader column={column} child="nodes.headers.name" />
    ),
    cell: (info) => info.getValue(),
    footer: (props) => props.column.id,
  },
  deveui: {
    id: "deveui",
    accessorKey: "deveui",
    header: ({ column }) => (
      <SearchHeader column={column} child="nodes.headers.deveui" />
    ),
    cell: (info) => info.getValue(),
    footer: (props) => props.column.id,
  },
  application: {
    id: "application",
    accessorKey: "application",
    header: ({ column }) => (
      <SearchHeader
        column={column}
        child="nodes.headers.application"
        combo={[]}
      />
    ),
    cell: (info) => (
      <Badge className="bg-gray-600 text-white">
        {(info.getValue() as Application).name}
      </Badge>
    ),
    footer: (props) => props.column.id,
    sortingFn: (rowA, rowB, columnId) => {
      const a = (rowA.getValue(columnId) as Application).name;
      const b = (rowB.getValue(columnId) as Application).name;
      return a.localeCompare(b);
    },
    filterFn: (row, id, filterValue) => {
      if (!filterValue || filterValue.length === 0) return true;

      const appName = (row.getValue(id) as Application).name;
      for (var f of filterValue) {
        if (appName === f) return true;
      }

      return false;
    },
  },
  battery: {
    id: "battery",
    accessorKey: "battery",
    header: ({ column }) => (
      <SearchHeader column={column} child="nodes.headers.battery" />
    ),
    cell: (info) => batteryDisplay(info.getValue() as string),
    footer: (props) => props.column.id,
  },
  lastContact: {
    id: "lastContact",
    accessorKey: "lastContact",
    header: ({ column }) => (
      <SearchHeader column={column} child="nodes.headers.lastContact" />
    ),

    cell: (info) => <DateTableDisplay dateString={info.getValue() as string} />,
    footer: (props) => props.column.id,
  },
  decoder: {
    id: "decoder",
    accessorKey: "decoder",
    header: ({ column }) => (
      <SearchHeader column={column} child="nodes.headers.decoder" />
    ),
    cell: (info) =>
      (info.getValue() as string) ? (
        <Badge className="bg-gray-600 text-white">
          {(info.getValue() as string)?.toUpperCase()}
        </Badge>
      ) : (
        <NaBadge />
      ),
  },
  rssi: {
    id: "rssi",
    accessorKey: "rssi",
    header: ({ column }) => (
      <SearchHeader column={column} child="nodes.headers.rssi" />
    ),
    cell: (info) => <RssiTableDisplay rssi={info.getValue() as number} />,
    footer: (props) => props.column.id,
  },
  active: {
    id: "active",
    accessorKey: "active",
    header: ({ column }) => (
      <SearchHeader
        column={column}
        child="nodes.headers.active"
        combo={["Status", "Active", "Inactive"]}
        isMulti={false}
        comboValue={useUiNodeState((state) => state.tableActiveStatusFilter)}
        setComboValue={useUiNodeState(
          (state) => state.setTableActiveStatusFilter,
        )}
      />
    ),
    cell: (info) => {
      const lastContact = info.row.original?.lastContact ?? "";
      const isRecentlyActive = isDateAfterHours(lastContact, 12);
      return isRecentlyActive ? (
        <CheckIcon size={24} className="text-green-600" />
      ) : (
        <UnplugIcon size={24} className="text-red-600" />
      );
    },

    filterFn: (row, _, filterValue) => {
      if (!filterValue || filterValue.length === 0) return true;
      if (filterValue == "Status") return true;

      const lastContact = row.original?.lastContact ?? "";
      const isRecentlyActive = isDateAfterHours(lastContact, 12);
      const filterValueBool = filterValue == "Active" ? true : false;
      return isRecentlyActive == filterValueBool;
    },
  },
};

export function getAttributeColumnDef(
  col: NodeTableColumnSettings,
): ColumnDef<any> {
  return {
    id: "attributeDict." + col.key,
    accessorKey: "attributeDict." + col.key,
    accessorFn: (_) => {
      // Used to suppress warnings for missing accessorKey this is currently the workaround for this
    },
    header: ({ column }) => (
      <SearchHeader
        column={column}
        child={col.title ?? ""}
        childIsText={true}
      />
    ),
    cell: (info) => {
      const attrIndex = info.row.original.attributes?.findIndex(
        (d: TagAttribute) =>
          d.normalizedName == col.key.replace("attributeDict.", ""),
      );
      const val =
        attrIndex != undefined && attrIndex >= 0
          ? info.row.original.attributes[attrIndex].value
          : null;
      if (val === null || val === undefined || val === "") {
        return <NaBadge />;
      }

      if (typeof val === "number") {
        return <Badge className="bg-gray-600 text-white">{val}</Badge>;
      } else if (typeof val === "string") {
        return val;
      } else if (typeof val === "boolean") {
        return val ? (
          <CheckIcon className="bg-green-600 text-white" />
        ) : (
          <CrossIcon className="bg-red-600 text-white" />
        );
      } else {
        return <NaBadge />;
      }
    },
  };
}

export function batteryDisplay(battery: string | null | undefined) {
  if (!battery) return <NaBadge />;
  const batteryLevel = parseFloat(battery);
  if (isNaN(batteryLevel)) return <NaBadge />;

  return (
    <Badge className="bg-gray-600 text-white">{batteryLevel.toFixed(2)}</Badge>
  );
}

export function RssiTableDisplay({ rssi }: { rssi: number | null }) {
  if (rssi === null) {
    return <SignalLow size={24} className="text-gray-600" />;
  } else if (rssi > -115) {
    return <SignalHigh size={24} className="text-green-600" />;
  } else if (rssi <= -120) {
    return <SignalLow size={24} className="text-red-600" />;
  } else {
    return <SignalMedium size={24} className="text-yellow-600" />;
  }
}

export function getColDefFromNodeTableColumns(
  cols: NodeTableColumnSettings[],
  actionCallback: (type: TableActionEventType, row: Row<any>) => void,
) {
  const res: ColumnDef<any>[] = [];
  res.push(nodeTableColumnsMap["select"]);

  for (var c of cols) {
    if (c.isAttribute) res.push(getAttributeColumnDef(c));

    const colDef = nodeTableColumnsMap[c.key];
    if (colDef) {
      res.push(colDef);
    }
  }

  // Add the defaults that should exist
  const isPermitted = useUserState.getState().isPermitted;

  const editPermission = isPermitted("Tag.Update");
  const deletePermission = isPermitted("Tag.Delete");
  res.push(
    ...getActionColDefs(
      "nodes",
      actionCallback,
      editPermission,
      deletePermission,
    ),
  );

  return res;
}

export function NodeTypeCell({ nodeType }: { nodeType: number }) {
  switch (nodeType) {
    case NodeTypeEnum.LORAWAN:
      return <Badge className={"bg-blue-600 text-white"}>LoRaWAN</Badge>;
    case NodeTypeEnum.API:
      return <Badge className={"bg-orange-600 text-white"}>API</Badge>;
    case NodeTypeEnum.MQTT:
      return <Badge className={"bg-green-600 text-white"}>MQTT</Badge>;
    case NodeTypeEnum.NODERED:
      return <Badge className={"bg-red-600 text-white"}>Node-RED</Badge>;
    case NodeTypeEnum.SPARKPLUG:
      return <Badge className={"bg-purple-600 text-white"}>Sparkplug</Badge>;
    case NodeTypeEnum.SPOTIO:
      return <Badge className="bg-green-600 text-white">SpotIO</Badge>;
    case NodeTypeEnum.MODBUS:
      return <Badge className="bg-yellow-600 text-white">Modbus</Badge>;
    case NodeTypeEnum.OPC:
      return <Badge className="bg-gray-600 text-white">OPC</Badge>;
    default:
      return <Badge color="gray">Unknown</Badge>;
  }
}

export function attributesToNodeTableColumns(
  attributes: TagAttribute[],
): NodeTableColumnSettings[] {
  return attributes.map(
    (a) =>
      ({
        index: -1,
        title: a.name,
        key: "attributeDict." + a.normalizedName,
        subgroup: "Attributes",
        isDefault: false,
        isAttribute: true,
        useLangLine: false,
        langLine: null,
        sortable: null,
      }) as NodeTableColumnSettings,
  );
}
